import React, { useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import Cookies from 'js-cookie';
import { withAuth } from "../../hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { FiShoppingBag, FiTrash2, FiArrowRight, FiPlus, FiMinus, FiArrowLeft } from "react-icons/fi";
import Alert from '@mui/material/Alert';

function Cart() {
  const { cart, updateCart } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [removingItemId, setRemovingItemId] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const navigate = useNavigate();
  const shippingPrice = 5.99;

  useEffect(() => {
    // Set first load to false after initial render
    setIsFirstLoad(false);
  }, []);

  const handleQuantityChange = (itemId, newQuantity, stock) => {
    if (newQuantity < 1) {
      handleRemoveItem(itemId);
    } else if (newQuantity <= stock) {
      const updatedCart = cart.map(item =>
        item.productId === itemId ? { ...item, quantity: newQuantity } : item
      );
      updateCart(updatedCart);
    }
  };

  const handleRemoveItem = (itemId) => {
    setRemovingItemId(itemId); // Set the item being removed for animation
    
    // Wait for animation to complete before actually removing
    setTimeout(() => {
      const updatedCart = cart.filter(item => item.productId !== itemId);
      updateCart(updatedCart);
      setRemovingItemId(null);
    }, 300);
  };

  async function createNewOrder(cartItems, currency) {
    const accessToken = Cookies.get('accessToken');
    let bodyData = [];

    cartItems.forEach(item => {
      bodyData.push({
        "id": item.productId,
        "quantity": item.quantity
      })
    });

    const body = {
      "currency": "eur",
      "cartItems": bodyData
    }

    const response = await fetch(process.env.REACT_APP_BACKEND_URI + `payments/create-payment-intent?accessToken=${accessToken}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  }

  async function handleCheckout(e) {
    e.preventDefault();
    setCheckoutLoading(true);
    setCheckoutError("");
    
    try {
      let response = await createNewOrder(cart, "eur");
      
      if (response && response.paymentId) {
        navigate(`/payment/${response.paymentId}`);
      }
    } catch (error) {
      console.error("Checkout failed", error);
      if (error instanceof Response) {
        const errorText = await error.text();
        setCheckoutError(errorText || "C'è stato un errore nella richiesta. Riprova più tardi.");
      } else {
        setCheckoutError("Impossibile effettuare il checkout. Riprova più tardi o contatta il supporto.");
      }
    } finally {
      setCheckoutLoading(false);
    }
  }

  const subtotal = cart.reduce((total, item) => total + item.productPrice * item.quantity, 0);
  const cartTotal = (subtotal + shippingPrice).toFixed(2);
  const vatAmount = (cartTotal * 0.22).toFixed(2);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } },
    exit: { opacity: 0, x: -20, height: 0, transition: { duration: 0.3 } }
  };

  const summaryVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 100, damping: 20, delay: 0.2 } }
  };

  const buttonVariants = {
    hover: { scale: 1.03, backgroundColor: "#202020" },
    tap: { scale: 0.97 },
    disabled: { opacity: 0.7, cursor: "not-allowed" }
  };

  return (
    <section className="relative min-h-screen bg-gray-50 w-full">
      <Layout>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="px-4 md:px-8 lg:px-16 py-8 w-full max-w-7xl mx-auto"
        >
          <div className="mt-24 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center mb-6"
            >
              <Link to="/store" className="text-gray-500 hover:text-gray-700 flex items-center">
                <FiArrowLeft className="mr-2" />
                <span>Torna allo Store</span>
              </Link>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-4xl font-bold text-gray-900 inria-serif-regular"
            >
              Il Tuo Carrello
            </motion.h1>
          </div>
          
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            {/* Cart Items */}
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="lg:col-span-2"
            >
              {cart.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white p-8 rounded-lg shadow-sm text-center border border-gray-100"
                >
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.3 }}
                    className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  >
                    <FiShoppingBag className="text-gray-400 w-10 h-10" />
                  </motion.div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Il tuo carrello è vuoto</h3>
                  <p className="text-gray-500 mb-6">Sembra che tu non abbia ancora aggiunto prodotti al carrello.</p>
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Link 
                      to="/store" 
                      className="inline-flex items-center px-6 py-3 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] transition-colors"
                    >
                      Inizia a Fare Acquisti <FiArrowRight className="ml-2" />
                    </Link>
                  </motion.div>
                </motion.div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Articoli ({cart.reduce((total, item) => total + item.quantity, 0)})
                    </h2>
                  </div>
                  <div className="divide-y divide-gray-100">
                    <AnimatePresence>
                      {cart.map((item) => (
                        <motion.div
                          key={item.productId}
                          variants={itemVariants}
                          initial={isFirstLoad ? "hidden" : false}
                          animate="visible"
                          exit="exit"
                          className={`p-6 ${removingItemId === item.productId ? 'opacity-50' : ''}`}
                        >
                          <div className="flex flex-col sm:flex-row gap-4">
                            {/* Product Image */}
                            <div className="w-24 h-24 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                              <motion.img 
                                whileHover={{ scale: 1.05 }}
                                src={item.imageUrl.startsWith('http') ? item.imageUrl : `https://res.cloudinary.com/dndm9hex2/image/upload/v1733331162/${item.imageUrl}`} 
                                alt={item.productName}
                                className="w-full h-full object-cover" 
                              />
                            </div>
                            
                            {/* Product Info */}
                            <div className="flex-grow">
                              <div className="flex flex-wrap justify-between">
                                <div>
                                  <h3 className="text-base font-medium text-gray-900">{item.productName}</h3>
                                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">{item.productDescription}</p>
                                </div>
                                <div className="text-right">
                                  <span className="text-lg font-bold text-gray-900">€{(item.productPrice * item.quantity).toFixed(2)}</span>
                                  <p className="text-sm text-gray-500">€{item.productPrice} per articolo</p>
                                </div>
                              </div>
                              
                              {/* Quantity and Remove Controls */}
                              <div className="flex flex-wrap items-center justify-between mt-4">
                                <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg">
                                  <motion.button
                                    whileHover={{ backgroundColor: "#f3f4f6" }}
                                    whileTap={{ scale: 0.95 }}
                                    disabled={item.quantity <= 1}
                                    onClick={() => handleQuantityChange(item.productId, item.quantity - 1)}
                                    className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <FiMinus size={16} />
                                  </motion.button>
                                  <span className="px-4 py-1 font-medium text-gray-900">{item.quantity}</span>
                                  <motion.button
                                    whileHover={{ backgroundColor: "#f3f4f6" }}
                                    whileTap={{ scale: 0.95 }}
                                    disabled={item.quantity >= (item.stock || item.quantityAvailable)}
                                    onClick={() => handleQuantityChange(item.productId, item.quantity + 1, item.stock || item.quantityAvailable)}
                                    className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <FiPlus size={16} />
                                  </motion.button>
                                </div>
                                
                                <motion.button
                                  whileHover={{ scale: 1.05, color: "#ef4444" }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => handleRemoveItem(item.productId)}
                                  className="flex items-center text-gray-500 hover:text-red-500 text-sm font-medium"
                                >
                                  <FiTrash2 className="mr-1" /> Rimuovi
                                </motion.button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </motion.div>
            
            {/* Order Summary */}
            <motion.div 
              variants={summaryVariants}
              initial="hidden"
              animate="visible"
              className="mt-8 lg:mt-0"
            >
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 sticky top-24">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Riepilogo Ordine</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotale</span>
                    <span>€{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Sconto</span>
                    <span>€0.00</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Spedizione</span>
                    <span>€{shippingPrice}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>IVA (22%)</span>
                    <span>€{vatAmount}</span>
                  </div>
                  <div className="pt-3 mt-3 border-t border-gray-100">
                    <div className="flex justify-between font-bold text-lg text-gray-900">
                      <span>Totale</span>
                      <span>€{cartTotal}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 space-y-4">
                  <motion.button
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap={cart.length === 0 ? null : "tap"}
                    animate={cart.length === 0 ? "disabled" : "visible"}
                    disabled={cart.length === 0 || checkoutLoading}
                    onClick={handleCheckout}
                    className="w-full py-3 px-4 bg-[#406580] text-white rounded-lg font-medium flex justify-center items-center disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {checkoutLoading ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.961 7.961 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Elaborazione...
                      </div>
                    ) : (
                      <>
                        Procedi all'acquisto <FiArrowRight className="ml-2" />
                      </>
                    )}
                  </motion.button>
                  
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="flex justify-center"
                  >
                    <Link to="/store" className="text-[#406580] hover:underline font-medium flex items-center">
                      Continua gli acquisti
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Error Alert */}
          <AnimatePresence>
            {checkoutError && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="fixed top-24 right-4 z-50 max-w-md"
              >
                <Alert
                  severity="error"
                  onClose={() => setCheckoutError("")}
                  className="shadow-lg border border-red-100"
                >
                  {checkoutError}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </Layout>
    </section>
  );
}

export default withAuth(Cart);